/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2221 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pluralize from "@monsma/XDL/Components/Common/Pluralize";
import React, { FC } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getPromotionsDataView(state, state.pages.orderConfirmation.cartId);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        cartState: getCartState(state, state.pages.orderConfirmation.cartId),
        orderPromotions,
        shippingPromotions,
        discountTotal,
        promotionsDataView,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemsCount?: TypographyPresentationProps;
}

export interface OrderConfirmationTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    // custom
    xOrderConfirmationTotal?: XStyles;
}

export const orderConfirmationTotalStyles: OrderConfirmationTotalStyles = {};

const styles = orderConfirmationTotalStyles;

const OrderConfirmationTotal: FC<Props> = props => {
    const styles = mergeToNew(orderConfirmationTotalStyles, orderConfirmationTotalStyles.xOrderConfirmationTotal);

    if (!props.cartState.value || !props.promotionsDataView.value) {
        return null;
    }

    // xng-change: Add the order summary label, and the count of orders to match the general style for this component on the site
    return (
        <>
            <Typography {...styles.title}>{translate("Order Summary")}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.itemsCount}>
                    <Pluralize
                        total={props.cartState.value?.cartLines?.length || 0}
                        pluralLabel={translate("Items")}
                        singularLabel={translate("Item")}
                    />
                </Typography>
                <CartTotalDisplay
                    cart={props.cartState.value}
                    orderPromotions={props.orderPromotions}
                    discountTotal={props.discountTotal}
                    shippingPromotions={props.shippingPromotions}
                    extendedStyles={styles.cartTotal}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderConfirmationTotal),
    definition: {
        group: "Order Confirmation",
        allowedContexts: ["OrderConfirmationPage"],
        displayName: "Order Confirmation Total",
    },
};

export default widgetModule;
